// frontend/src/pages/ProfilePage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Heart, Star, Bookmark, Calendar, Edit2, LogOut, Film,
  TrendingUp, Award, BarChart, Settings, Share2, Loader,
  MessageSquare, Download, X, Users,
  UserIcon, BookOpen, Trash2, Sparkles
} from 'lucide-react';
import { User } from '../types';
import { useAppContext } from '../context/AppContext';
import MovieCard from '../components/MovieCard';
import { moviesAPI, reviewsAPI, userAPI } from '../services/api';
import { useParams } from 'react-router-dom';

// ─────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────
interface MovieItem {
  id: number;
  title: string;
  overview?: string;
  posterUrl?: string;
  releaseYear?: number;
  voteAverage?: number;
  season?: string;
  holiday?: string;
  moods?: string[];
  tropes?: string[];
  favoritesCount?: number;
  reviewsCount?: number;
}

interface ReviewItem {
  id: string;
  content: string;
  rating: number;
  createdAt: string;
  likesCount: number;
  commentsCount: number;
  movie: {
    id: number;
    title: string;
    posterUrl?: string;
    releaseYear?: number;
  };
}

interface NotebookEntryItem {
  id: string;
  watchedOn?: string;
  loggedAt: string;
  note: string | null;
  movie: MovieItem;
}

// ─────────────────────────────────────────────
// SHARE CARD COMPONENT
// ─────────────────────────────────────────────
const ShareCard: React.FC<{
  user: any;
  favoritesCount: number;
  watchlistCount: number;
  reviewsCount: number;
  avgRating: number;
  topMovie?: MovieItem;
  onClose: () => void;
}> = ({ user, favoritesCount, watchlistCount, reviewsCount, avgRating, topMovie, onClose }) => {
  const cardRef = useRef<HTMLDivElement>(null);

  const handleDownload = async () => {
    if (!cardRef.current) return;
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(cardRef.current, { scale: 2, useCORS: true } as any);
      const link = document.createElement('a');
      link.download = `${user.username}-romcom-profile.png`;
      link.href = canvas.toDataURL();
      link.click();
    } catch (err) {
      console.error('Failed to generate share card:', err);
      alert('Could not generate image. Try screenshotting instead!');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
      >
        <div
          ref={cardRef}
          className="bg-gradient-to-br from-romcom-pink via-romcom-red to-romcom-purple p-8 text-white"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-full bg-[#faf8f6] flex items-center justify-center text-2xl font-bold">
              {(user.name || user.username || 'U').charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="text-2xl font-playfair font-bold">{user.name || user.username}</h2>
              <p className="opacity-80 text-sm">@{user.username}</p>
            </div>
            <div className="ml-auto text-3xl">💕</div>
          </div>

          <div className="text-center mb-6">
            <p className="text-xl font-playfair italic opacity-90">ROMCOMTHIS</p>
            <p className="text-sm opacity-70 mt-1">My Romcom Profile</p>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-white/20 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold">{favoritesCount}</div>
              <div className="text-sm opacity-80">❤️ Favorites</div>
            </div>
            <div className="bg-white/20 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold">{watchlistCount}</div>
              <div className="text-sm opacity-80">🔖 Watchlist</div>
            </div>
            <div className="bg-white/20 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold">{reviewsCount}</div>
              <div className="text-sm opacity-80">💬 Reviews</div>
            </div>
            <div className="bg-white/20 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold">{avgRating > 0 ? avgRating.toFixed(1) : '—'}</div>
              <div className="text-sm opacity-80">⭐ Avg Rating</div>
            </div>
          </div>

          {topMovie && (
            <div className="bg-white/20 rounded-xl p-4 text-center mb-4">
              <p className="text-sm opacity-80 mb-1">🎬 Currently Loving</p>
              <p className="font-bold text-lg">{topMovie.title}</p>
              {topMovie.releaseYear && (
                <p className="text-sm opacity-70">({topMovie.releaseYear})</p>
              )}
            </div>
          )}

          <p className="text-center text-xs opacity-60 mt-4">romcomthis.com</p>
        </div>

        <div className="p-4 flex gap-3">
          <button
            onClick={handleDownload}
            className="flex-1 flex items-center justify-center gap-2 bg-romcom-red text-white py-3 rounded-xl font-medium hover:bg-red-600 transition-colors"
          >
            <Download className="w-5 h-5" />
            Download Card
          </button>
          <button
            onClick={onClose}
            className="p-3 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─────────────────────────────────────────────
// STATIC STAT BAR
// ─────────────────────────────────────────────
const StatBar: React.FC<{
  label: string;
  value: string | number;
  pct: number;
  color?: string;
}> = ({ label, value, pct, color = 'from-romcom-pink to-romcom-purple' }) => (
  <div>
    <div className="flex justify-between mb-1 text-sm">
      <span className="text-gray-600">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${Math.min(pct, 100)}%` }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        className={`h-full bg-gradient-to-r ${color}`}
      />
    </div>
  </div>
);

// ─────────────────────────────────────────────
// MAIN PROFILE PAGE
// ─────────────────────────────────────────────
const ProfilePage: React.FC = () => {
  const { state, logout, updateProfile, openLoginModal } = useAppContext();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<'favorites' | 'watchlist' | 'notebook' | 'reviews' | 'followers' | 'following'>('favorites');
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [bioText, setBioText] = useState(state.currentUser?.bio || '');
  const [showShareCard, setShowShareCard] = useState(false);
  const { username } = useParams<{ username: string }>();

  const [profileUser, setProfileUser] = useState<User | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);

  // Data state
  const [favorites, setFavorites] = useState<MovieItem[]>([]);
  const [memoryLane, setMemoryLane] = useState<MovieItem[]>([]);
  const [notebookEntries, setNotebookEntries] = useState<NotebookEntryItem[]>([]);
  const [reviews, setReviews] = useState<ReviewItem[]>([]);
  const [followers, setFollowers] = useState<any[]>([]);
  const [following, setFollowing] = useState<any[]>([]);
  const [isLoadingFavorites, setIsLoadingFavorites] = useState(false);
  const [isLoadingMemoryLane, setIsLoadingMemoryLane] = useState(false);
  const [isLoadingNotebook, setIsLoadingNotebook] = useState(false);
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);
  const [isLoadingFollowers, setIsLoadingFollowers] = useState(false);
  const [isLoadingFollowing, setIsLoadingFollowing] = useState(false);

  // ── Fetch profile user ──
  useEffect(() => {
    const fetchProfileUser = async () => {
      try {
        setIsLoadingProfile(true);
        if (username) {
          const res = await userAPI.getPublicProfile(username);
          setProfileUser(res.data.data);
        } else if (state.currentUser) {
          setProfileUser(state.currentUser);
        } else {
          setProfileUser(null);
        }
      } catch (err) {
        console.error('Failed to fetch profile:', err);
        setProfileUser(null);
      } finally {
        setIsLoadingProfile(false);
      }
    };
    fetchProfileUser();
  }, [username, state.currentUser]);

  // ── Fetch favorites ──
  useEffect(() => {
    if (!profileUser || !state.currentUser || profileUser.id !== state.currentUser.id) return;
    const fetch = async () => {
      try {
        setIsLoadingFavorites(true);
        const res = await moviesAPI.getUserFavorites();
        setFavorites(res.data.data);
      } catch (err) {
        console.error('Failed to fetch favorites:', err);
      } finally {
        setIsLoadingFavorites(false);
      }
    };
    fetch();
  }, [profileUser, state.currentUser, username]);

  // ── Fetch Memory Lane ──
  useEffect(() => {
    if (!profileUser || !profileUser.username) return;
    const fetch = async () => {
      try {
        setIsLoadingMemoryLane(true);
        const res = await userAPI.getPublicWatched(profileUser.username!);
        setMemoryLane(res.data.data);
      } catch (err) {
        console.error('Failed to fetch memory lane:', err);
      } finally {
        setIsLoadingMemoryLane(false);
      }
    };
    fetch();
  }, [profileUser]);

  // ── Fetch Notebook ──
  useEffect(() => {
    if (!profileUser || !profileUser.username) return;
    const fetch = async () => {
      try {
        setIsLoadingNotebook(true);
        const res = await userAPI.getPublicNotebook(profileUser.username!);
        setNotebookEntries(res.data.data || []);
      } catch (err) {
        console.error('Failed to fetch notebook:', err);
      } finally {
        setIsLoadingNotebook(false);
      }
    };
    fetch();
  }, [profileUser]);

  const handleDeleteNotebookEntry = async (entryId: string) => {
    try {
      await moviesAPI.deleteNotebookEntry(entryId);
      setNotebookEntries(prev => prev.filter(e => e.id !== entryId));
    } catch (err) {
      console.error('Failed to delete notebook entry:', err);
    }
  };

  // ── Fetch reviews ──
  useEffect(() => {
    if (!profileUser || !state.currentUser || profileUser.id !== state.currentUser.id) return;
    const fetch = async () => {
      try {
        setIsLoadingReviews(true);
        const res = await reviewsAPI.getUserReviews();
        setReviews(res.data.data);
      } catch (err) {
        console.error('Failed to fetch reviews:', err);
      } finally {
        setIsLoadingReviews(false);
      }
    };
    fetch();
  }, [profileUser, state.currentUser, username]);

  // ── Fetch followers ──
  useEffect(() => {
    if (!profileUser) return;
    const fetch = async () => {
      try {
        setIsLoadingFollowers(true);
        const res = await userAPI.getFollowers(profileUser.id);
        setFollowers(res.data.data);
      } catch (err) {
        console.error('Failed to fetch followers:', err);
      } finally {
        setIsLoadingFollowers(false);
      }
    };
    fetch();
  }, [profileUser, state.currentUser]);

  // ── Fetch following ──
  useEffect(() => {
    if (!profileUser) return;
    const fetch = async () => {
      try {
        setIsLoadingFollowing(true);
        const res = await userAPI.getFollowing(profileUser.id);
        setFollowing(res.data.data);
      } catch (err) {
        console.error('Failed to fetch following:', err);
      } finally {
        setIsLoadingFollowing(false);
      }
    };
    fetch();
  }, [profileUser, state.currentUser]);

  if (isLoadingProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="w-8 h-8 text-romcom-red animate-spin" />
      </div>
    );
  }

  if (!profileUser) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <UserIcon className="w-24 h-24 text-gray-300 mx-auto mb-6" />
          <h1 className="text-4xl font-bold mb-4">
            {username ? 'User not found' : 'Sign in to view your profile'}
          </h1>
          {!username && (
            <>
              <p className="text-gray-600 mb-8">Create an account to track your romcom journey</p>
              <button onClick={openLoginModal} className="btn-primary text-lg px-8 py-3">
                Sign In / Sign Up
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  const user = profileUser;

  // ── Stats (live) ──
  const avgRating = reviews.length > 0
    ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
    : 0;

  // ── Seasonal counts (live from favorites) ──
  const seasonCounts = favorites.reduce((acc: Record<string, number>, movie) => {
    if (movie.season) acc[movie.season] = (acc[movie.season] || 0) + 1;
    return acc;
  }, {});

  // ── Rating distribution (live) ──
  const ratingDist: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  reviews.forEach(r => {
    const key = Math.round(r.rating) as 1 | 2 | 3 | 4 | 5;
    if (key >= 1 && key <= 5) ratingDist[key]++;
  });
  const maxRatingCount = Math.max(...Object.values(ratingDist), 1);

  const topMovie = favorites[0];

  const handleSaveBio = async () => {
    await updateProfile({ bio: bioText });
    setIsEditingBio(false);
  };

  const tabs = [
    { key: 'favorites', label: 'The Vault', icon: Heart, count: favorites.length },
    { key: 'watchlist', label: 'Memory Lane', icon: Bookmark, count: memoryLane.length },
    { key: 'notebook', label: 'The Notebook', icon: BookOpen, count: notebookEntries.length },
    { key: 'reviews', label: 'Reviews', icon: MessageSquare, count: reviews.length },
    { key: 'following', label: 'Following', icon: Users, count: user.followingCount || 0 },
    { key: 'followers', label: 'Followers', icon: Users, count: user.followersCount || 0 },
  ] as const;

  return (
    <div className="min-h-screen bg-gradient-to-br from-romcom-pink/10 to-romcom-blue/10">

      <AnimatePresence>
        {showShareCard && (
          <ShareCard
            user={user}
            favoritesCount={favorites.length}
            watchlistCount={memoryLane.length}
            reviewsCount={reviews.length}
            avgRating={avgRating}
            topMovie={topMovie}
            onClose={() => setShowShareCard(false)}
          />
        )}
      </AnimatePresence>

      <div className="container mx-auto px-4 pt-24 pb-16">

        {/* ── PROFILE HEADER ── */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-romcom-pink/20 to-romcom-blue/20 rounded-3xl p-8 mb-8"
        >
          <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-6">
            <div className="flex items-center gap-6">
              <div className="relative flex-shrink-0">
                <div className="w-28 h-28 bg-gradient-to-r from-romcom-pink to-romcom-red rounded-full flex items-center justify-center text-white text-4xl font-bold shadow-lg">
                  {user.avatar ? (
                    <img src={user.avatar} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    (user.name || user.username || 'U').charAt(0).toUpperCase()
                  )}
                </div>
                <button className="absolute bottom-1 right-1 bg-white p-1.5 rounded-full shadow-md hover:scale-110 transition-transform">
                  <Edit2 className="w-3 h-3 text-gray-600" />
                </button>
              </div>

              <div>
                <h1 className="text-3xl font-playfair font-bold text-gray-800 mb-1">
                  {user.name || user.username}
                </h1>
                <p className="text-gray-500 mb-2">@{user.username}</p>
                <div className="flex items-center text-gray-500 text-sm mb-3">
                  <Calendar className="w-4 h-4 mr-1" />
                  Joined {new Date(user.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </div>

                {isEditingBio ? (
                  <div>
                    <textarea
                      value={bioText}
                      onChange={e => setBioText(e.target.value)}
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-romcom-red focus:border-transparent text-sm"
                      rows={2}
                      placeholder="Tell us about your romcom journey..."
                    />
                    <div className="flex gap-2 mt-2">
                      <button onClick={handleSaveBio} className="btn-primary text-sm px-4 py-1.5">Save</button>
                      <button onClick={() => { setIsEditingBio(false); setBioText(user.bio || ''); }} className="btn-secondary text-sm px-4 py-1.5">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-gray-600 text-sm">
                      {user.bio || 'No bio yet. Tell us about your romcom journey!'}
                    </p>
                    <button onClick={() => setIsEditingBio(true)} className="text-romcom-red hover:underline text-xs mt-1">
                      {user.bio ? 'Edit bio' : '+ Add bio'}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 flex-shrink-0">
              {(!username && state.currentUser) ? (
                <>
                  <button
                    onClick={() => navigate('/settings')}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm"
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </button>
                  <button
                    onClick={logout}
                    className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 text-sm"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </>
              ) : (
                <button className="flex items-center gap-2 px-6 py-2 bg-romcom-red text-white rounded-lg hover:bg-red-600 text-sm font-medium">
                  <Users className="w-4 h-4" />
                  Follow
                </button>
              )}
            </div>
          </div>
        </motion.div>

        {/* ── STATS OVERVIEW ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8"
        >
          {[
            { icon: Heart, label: 'The Vault', value: favorites.length, color: 'text-romcom-red', bg: 'bg-red-50' },
            { icon: Bookmark, label: 'Memory Lane', value: memoryLane.length, color: 'text-yellow-500', bg: 'bg-yellow-50' },
            { icon: MessageSquare, label: 'Reviews', value: reviews.length, color: 'text-blue-500', bg: 'bg-blue-50' },
            { icon: Users, label: 'Following', value: user.followingCount || 0, color: 'text-green-500', bg: 'bg-green-50' },
            { icon: Users, label: 'Followers', value: user.followersCount || 0, color: 'text-indigo-500', bg: 'bg-indigo-50' },
            { icon: Star, label: 'Avg Rating', value: avgRating > 0 ? `${avgRating.toFixed(1)}★` : '—', color: 'text-purple-500', bg: 'bg-purple-50' },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.05 }}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center"
            >
              <div className={`${stat.bg} w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3`}>
                <stat.icon className={`w-6 h-6 ${stat.color}`} />
              </div>
              <div className="text-3xl font-bold text-gray-800 mb-1">{stat.value}</div>
              <div className="text-gray-500 text-sm">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* ── MAIN CONTENT ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Left: tabs */}
          <div className="lg:col-span-2">

            <div className="flex flex-wrap gap-2 mb-6">
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-sm transition-all ${
                    activeTab === tab.key
                      ? 'bg-romcom-red text-white shadow-md'
                      : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    activeTab === tab.key ? 'bg-white/20' : 'bg-gray-100'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>

            <AnimatePresence mode="wait">

              {/* The Vault */}
              {activeTab === 'favorites' && (
                <motion.div key="favorites" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                  {isLoadingFavorites ? (
                    <div className="flex justify-center py-16"><Loader className="w-8 h-8 text-romcom-red animate-spin" /></div>
                  ) : favorites.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {favorites.map(movie => <MovieCard key={movie.id} movie={movie} />)}
                    </div>
                  ) : (
                    <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
                      <Heart className="w-12 h-12 text-gray-200 mx-auto mb-4" />
                      <p className="text-gray-500 mb-4">The ones that stayed. محفوظ</p>
                      <button onClick={() => navigate('/movies')} className="btn-primary">Browse Movies</button>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Memory Lane */}
              {activeTab === 'watchlist' && (
                <motion.div key="watchlist" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                  {isLoadingMemoryLane ? (
                    <div className="flex justify-center py-16"><Loader className="w-8 h-8 text-romcom-red animate-spin" /></div>
                  ) : memoryLane.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {memoryLane.map(movie => <MovieCard key={movie.id} movie={movie} />)}
                    </div>
                  ) : (
                    <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
                      <Bookmark className="w-12 h-12 text-gray-200 mx-auto mb-4" />
                      <p className="text-gray-500 mb-4">Hold on to the ones that stayed</p>
                      <button onClick={() => navigate('/movies')} className="btn-primary">Find Movies to Add</button>
                    </div>
                  )}
                </motion.div>
              )}

              {/* The Notebook */}
              {activeTab === 'notebook' && (
                <motion.div key="notebook" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                  {isLoadingNotebook ? (
                    <div className="flex justify-center py-16"><Loader className="w-8 h-8 text-romcom-red animate-spin" /></div>
                  ) : notebookEntries.length > 0 ? (
                    <div className="space-y-3">
                      {notebookEntries.map(entry => (
                        <div key={entry.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex gap-4 items-start">
                          <button type="button" onClick={() => navigate(`/movies/${entry.movie.id}`)} className="w-14 h-20 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer">
                            {entry.movie.posterUrl ? (
                              <img src={entry.movie.posterUrl} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-romcom-pink to-romcom-purple flex items-center justify-center">
                                <Film className="w-6 h-6 text-white" />
                              </div>
                            )}
                          </button>
                          <div className="flex-1 min-w-0">
                            <button type="button" onClick={() => navigate(`/movies/${entry.movie.id}`)} className="font-bold text-gray-800 text-left hover:text-romcom-red transition-colors">
                              {entry.movie.title}
                            </button>
                            <p className="text-xs text-romcom-red/90 font-medium mt-0.5">
                              Watched on{' '}
                              {new Date(entry.watchedOn || entry.loggedAt).toLocaleDateString(undefined, {
                                weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
                              })}
                            </p>
                            <p className="text-[10px] text-gray-400 mt-0.5">
                              Logged {new Date(entry.loggedAt).toLocaleString()}
                            </p>
                            {entry.note && (
                              <p className="text-sm text-gray-600 mt-2 line-clamp-3">{entry.note}</p>
                            )}
                          </div>
                          <button type="button" onClick={() => handleDeleteNotebookEntry(entry.id)} className="p-2 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0" title="Remove log">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
                      <BookOpen className="w-12 h-12 text-gray-200 mx-auto mb-4" />
                      <p className="text-gray-500 mb-4">Empty...for now. Your notebook awaits.</p>
                      <button onClick={() => navigate('/movies')} className="btn-primary">Browse Movies</button>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Reviews */}
              {activeTab === 'reviews' && (
                <motion.div key="reviews" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                  {isLoadingReviews ? (
                    <div className="flex justify-center py-16"><Loader className="w-8 h-8 text-romcom-red animate-spin" /></div>
                  ) : reviews.length > 0 ? (
                    <div className="space-y-4">
                      {reviews.map(review => (
                        <div key={review.id} onClick={() => navigate(`/movies/${review.movie.id}`)} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:border-romcom-pink/30 cursor-pointer transition-all">
                          <div className="flex gap-4">
                            <div className="w-14 h-20 rounded-lg overflow-hidden flex-shrink-0">
                              {review.movie.posterUrl ? (
                                <img src={review.movie.posterUrl} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-romcom-pink to-romcom-purple flex items-center justify-center">
                                  <Film className="w-6 h-6 text-white" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between mb-1">
                                <h4 className="font-bold text-gray-800 truncate">{review.movie.title}</h4>
                                <span className="text-xs text-gray-400 ml-2 flex-shrink-0">{new Date(review.createdAt).toLocaleDateString()}</span>
                              </div>
                              <div className="flex items-center gap-1 mb-2">
                                {[...Array(5)].map((_, i) => (
                                  <Star key={i} className={`w-3.5 h-3.5 ${i < review.rating ? 'text-yellow-400 fill-current' : 'text-gray-200'}`} />
                                ))}
                                <span className="text-xs text-gray-500 ml-1">{review.rating}/5</span>
                              </div>
                              <p className="text-gray-600 text-sm line-clamp-2">{review.content}</p>
                              <div className="flex gap-3 mt-2 text-xs text-gray-400">
                                <span>❤️ {review.likesCount}</span>
                                <span>💬 {review.commentsCount}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
                      <MessageSquare className="w-12 h-12 text-gray-200 mx-auto mb-4" />
                      <p className="text-gray-500 mb-4">No reviews yet!</p>
                      <button onClick={() => navigate('/movies')} className="btn-primary">Review a Movie</button>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Following */}
              {activeTab === 'following' && (
                <motion.div key="following" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                  {isLoadingFollowing ? (
                    <div className="flex justify-center py-16"><Loader className="w-8 h-8 text-romcom-red animate-spin" /></div>
                  ) : following.length > 0 ? (
                    <div className="space-y-3">
                      {following.map((user: any) => (
                        <div key={user.id} onClick={() => navigate(`/users/${user.username}`)} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:border-romcom-pink/30 cursor-pointer transition-all flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-r from-romcom-pink to-romcom-red flex items-center justify-center text-white font-bold flex-shrink-0">
                            {user.avatar ? <img src={user.avatar} alt="" className="w-full h-full rounded-full object-cover" /> : (user.name || user.username || 'U').charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-800 truncate">{user.name || user.username}</p>
                            <p className="text-sm text-gray-500 truncate">@{user.username}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
                      <Users className="w-12 h-12 text-gray-200 mx-auto mb-4" />
                      <p className="text-gray-500 mb-4">Not following anyone yet!</p>
                      <button onClick={() => navigate('/movies')} className="btn-primary">Discover Movies</button>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Followers */}
              {activeTab === 'followers' && (
                <motion.div key="followers" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                  {isLoadingFollowers ? (
                    <div className="flex justify-center py-16"><Loader className="w-8 h-8 text-romcom-red animate-spin" /></div>
                  ) : followers.length > 0 ? (
                    <div className="space-y-3">
                      {followers.map((user: any) => (
                        <div key={user.id} onClick={() => navigate(`/users/${user.username}`)} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:border-romcom-pink/30 cursor-pointer transition-all flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-r from-romcom-pink to-romcom-red flex items-center justify-center text-white font-bold flex-shrink-0">
                            {user.avatar ? <img src={user.avatar} alt="" className="w-full h-full rounded-full object-cover" /> : (user.name || user.username || 'U').charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-800 truncate">{user.name || user.username}</p>
                            <p className="text-sm text-gray-500 truncate">@{user.username}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
                      <Users className="w-12 h-12 text-gray-200 mx-auto mb-4" />
                      <p className="text-gray-500 mb-4">No followers yet!</p>
                      <button onClick={() => navigate('/movies')} className="btn-primary">Share Your Reviews</button>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* ── RIGHT SIDEBAR ── */}
          <div className="space-y-6">

            {/* Top Favorite */}
            {topMovie && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-yellow-500" />
                  Top Favorite
                </h3>
                <div onClick={() => navigate(`/movies/${topMovie.id}`)} className="cursor-pointer group">
                  <div className="aspect-[2/3] rounded-xl overflow-hidden mb-3 shadow-md">
                    {topMovie.posterUrl ? (
                      <img src={topMovie.posterUrl} alt={topMovie.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-romcom-purple to-romcom-blue flex items-center justify-center">
                        <Film className="w-12 h-12 text-white" />
                      </div>
                    )}
                  </div>
                  <p className="font-bold text-gray-800 group-hover:text-romcom-red transition-colors">{topMovie.title}</p>
                  {topMovie.releaseYear && <p className="text-gray-500 text-sm">({topMovie.releaseYear})</p>}
                </div>
              </motion.div>
            )}

            {/* ── REVIEW STATS (live) ── */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <BarChart className="w-5 h-5 text-green-500" />
                Review Stats
              </h3>
              <div className="space-y-4">
                {/* Average rating */}
                <StatBar
                  label="Average Rating"
                  value={avgRating > 0 ? `${avgRating.toFixed(1)}/5` : '—'}
                  pct={avgRating > 0 ? (avgRating / 5) * 100 : 0}
                  color="from-green-400 to-green-500"
                />
                {/* Review frequency */}
                <StatBar
                  label="Review Frequency"
                  value={favorites.length > 0 ? `${Math.round((reviews.length / favorites.length) * 100)}%` : '0%'}
                  pct={favorites.length > 0 ? (reviews.length / favorites.length) * 100 : 0}
                  color="from-blue-400 to-blue-500"
                />
                {/* Rating breakdown */}
                {reviews.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-600 mb-2 font-medium">Rating Breakdown</p>
                    <div className="space-y-1.5">
                      {[5, 4, 3, 2, 1].map(star => (
                        <div key={star} className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 w-3">{star}</span>
                          <Star className="w-3 h-3 text-yellow-400 fill-current flex-shrink-0" />
                          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${(ratingDist[star] / maxRatingCount) * 100}%` }}
                              transition={{ duration: 0.7, ease: 'easeOut', delay: 0.1 * (6 - star) }}
                              className="h-full bg-gradient-to-r from-yellow-300 to-yellow-500 rounded-full"
                            />
                          </div>
                          <span className="text-xs text-gray-400 w-3 text-right">{ratingDist[star]}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>

            {/* ── SEASONAL PREFERENCES (static display, computed from favorites) ── */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-bold text-gray-800 mb-1 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-purple-500" />
                Seasonal Preferences
              </h3>
              <p className="text-xs text-gray-400 mb-4">Based on your Vault favorites</p>
              <div className="space-y-3">
                {[
                  { season: 'winter', icon: '❄️', color: 'from-blue-300 to-blue-500' },
                  { season: 'spring', icon: '🌸', color: 'from-pink-300 to-pink-500' },
                  { season: 'summer', icon: '☀️', color: 'from-yellow-300 to-orange-400' },
                  { season: 'autumn', icon: '🍂', color: 'from-orange-300 to-red-400' },
                ].map(({ season, icon, color }) => {
                  const count = seasonCounts[season] || 0;
                  const pct = favorites.length > 0 ? (count / favorites.length) * 100 : 0;
                  return (
                    <StatBar
                      key={season}
                      label={`${icon} ${season.charAt(0).toUpperCase() + season.slice(1)}`}
                      value={count}
                      pct={pct}
                      color={color}
                    />
                  );
                })}
                {favorites.length === 0 && (
                  <p className="text-xs text-gray-400 text-center py-2">Add movies to The Vault to see your seasonal taste</p>
                )}
              </div>
            </motion.div>

            {/* ── SHIP RATING TASTE (static flavor card) ── */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} className="bg-gradient-to-br from-romcom-pink/10 to-white rounded-xl p-6 shadow-sm border border-romcom-pink/20">
              <h3 className="text-lg font-bold text-gray-800 mb-1 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-romcom-red" />
                Your Ship Type
              </h3>
              <p className="text-xs text-gray-400 mb-4">Based on your average rating</p>
              {avgRating === 0 ? (
                <p className="text-sm text-gray-400 italic">Rate some movies to discover your ship type!</p>
              ) : avgRating >= 4.5 ? (
                <div>
                  <p className="text-2xl mb-1">💒</p>
                  <p className="font-bold text-romcom-red">The OTP Devotee</p>
                  <p className="text-xs text-gray-500 mt-1">You fall hard and you fall fast. Every couple is your forever couple.</p>
                </div>
              ) : avgRating >= 3.5 ? (
                <div>
                  <p className="text-2xl mb-1">💘</p>
                  <p className="font-bold text-romcom-red">The Hopeful Romantic</p>
                  <p className="text-xs text-gray-500 mt-1">You love love, but you know when chemistry just isn't there.</p>
                </div>
              ) : avgRating >= 2.5 ? (
                <div>
                  <p className="text-2xl mb-1">⚓</p>
                  <p className="font-bold text-gray-700">The Discerning Shipper</p>
                  <p className="text-xs text-gray-500 mt-1">You need real spark. Not every couple earns your heart.</p>
                </div>
              ) : (
                <div>
                  <p className="text-2xl mb-1">🌱</p>
                  <p className="font-bold text-gray-700">The Slow Burn Fan</p>
                  <p className="text-xs text-gray-500 mt-1">Cautious, thoughtful. You let love prove itself before you ship it.</p>
                </div>
              )}
            </motion.div>

            {/* Share card */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="bg-gradient-to-r from-romcom-pink to-romcom-red rounded-xl p-6 text-white">
              <h3 className="text-lg font-bold mb-2 flex items-center gap-2">
                <Share2 className="w-5 h-5" />
                Share Your Profile
              </h3>
              <p className="text-sm opacity-90 mb-4">Show off your romcom taste to friends!</p>
              <button
                onClick={() => setShowShareCard(true)}
                className="w-full bg-white text-romcom-red py-2.5 rounded-lg font-semibold hover:bg-white/90 transition-colors"
              >
                Generate Share Card 💕
              </button>
            </motion.div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;